import React, { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import type { ProjectImage } from "@/pages/Projects";

interface ProjectLightboxProps {
  project: ProjectImage | null;
  index: number;
  onClose: () => void;
  onNavigate: (newIndex: number) => void;
}

export const ProjectLightbox: React.FC<ProjectLightboxProps> = ({
  project,
  index,
  onClose,
  onNavigate,
}) => {
  const gallery = project?.gallery ?? (project ? [project.url] : []);
  const total = gallery.length;

  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNavigate((index + 1) % total);
      if (e.key === "ArrowLeft") onNavigate((index - 1 + total) % total);
    },
    [index, total, onClose, onNavigate],
  );

  useEffect(() => {
    if (!project) return;
    window.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [project, handleKey]);

  if (!project) return null;

  const currentImg = gallery[index] ?? project.url;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${project.title} photo gallery`}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        aria-label="Close gallery"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Previous */}
      {total > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate((index - 1 + total) % total);
          }}
          className="absolute left-2 sm:left-6 z-10 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          aria-label="Previous photo"
        >
          <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
        </button>
      )}

      {/* Image */}
      <div
        className="relative max-w-5xl w-full max-h-[85vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={currentImg}
          alt={`${project.title} — photo ${index + 1} of ${total}`}
          className="max-h-[78vh] w-auto max-w-full object-contain rounded-lg shadow-2xl"
        />
        <div className="mt-4 text-center">
          <h3 className="text-white font-bold text-lg">{project.title}</h3>
          <p className="text-stone-400 text-sm mt-1">
            {project.location} · {project.system}
          </p>
          {total > 1 && (
            <p className="text-[#C9A84C] text-xs font-semibold mt-2">
              {index + 1} / {total}
            </p>
          )}
        </div>
      </div>

      {/* Next */}
      {total > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate((index + 1) % total);
          }}
          className="absolute right-2 sm:right-6 z-10 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          aria-label="Next photo"
        >
          <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
        </button>
      )}

      {/* Thumbnails */}
      {total > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {gallery.map((gImg, gIdx) => (
            <button
              key={gIdx}
              onClick={(e) => {
                e.stopPropagation();
                onNavigate(gIdx);
              }}
              className={`w-12 h-12 rounded-md overflow-hidden border-2 transition-all ${
                gIdx === index
                  ? "border-[#C9A84C] scale-110"
                  : "border-transparent opacity-60 hover:opacity-100"
              }`}
              aria-label={`View photo ${gIdx + 1}`}
            >
              <img
                src={gImg}
                alt={`Thumbnail ${gIdx + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
